import { useEffect } from "react";
import { useDialogFocus } from "@givehere/ui";

/**
 * What is asked before the browser asks — `ADDENDUM.md` screen 5, rendered as Lantern's `2b`.
 *
 * ## Why the sentence moved off the directory
 *
 * It used to sit under the locate control as a `<small>`, which put the whole disclosure on the
 * front door of the product where it competed with the list a donor came to read. The addendum's
 * answer is better on both counts: the explanation appears at the moment it is needed — the tap —
 * and the directory gets the room back. Nothing is disclosed later than before, because the
 * browser has not been asked while this is on screen.
 *
 * ## Why it is a sheet and not an alert
 *
 * Deliberately unlike the centred prompt that follows it. Two dialogs of the same shape back to
 * back read as one dialog that must be dismissed twice, and a donor clicking through the second
 * because they clicked through the first has consented to nothing. A bottom sheet in the
 * product's own type is visibly *ours*; the browser's box after it is visibly the browser's.
 *
 * ## The rules it keeps
 *
 * - Reads once, never stored. Nothing here is written down; declining leaves no flag anywhere.
 * - Equal-weight actions, because the sentence above them says declining loses nothing — and a
 *   quiet link beside a filled green button would say the opposite.
 * - Escape declines. A lock is not dismissible by its scrim; this is not a lock.
 */
export function NearbyExplainer({
  onAllow,
  onDecline,
}: {
  onAllow: () => void;
  onDecline: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDecline();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDecline]);

  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="See what's near you?"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDecline();
      }}
    >
      <div className="sheet__panel nearby lattice-edge" ref={panelRef} tabIndex={-1}>
        <h2 className="nearby__title">See what&apos;s near you?</h2>
        <p className="nearby__body">
          Your location is read <strong>once</strong>, only to mark what&apos;s within about a mile.
          It is never stored, and it never leaves your device as a saved location.
        </p>
        <p className="nearby__body nearby__body--quiet">Search works either way.</p>
        <div className="nearby__actions">
          <button type="button" className="pay pay--outline" onClick={onDecline}>
            Not now
          </button>
          <button type="button" className="pay" onClick={onAllow}>
            Allow once
          </button>
        </div>
        <p className="nearby__after">Your browser asks next.</p>
      </div>
    </div>
  );
}
